<template>
  <div class="alumni-form">
    <form
      ref="profilePictureForm"
      class="profile-picture-upload"
      :class="{ '-error': profilePictureError }"
      action="/api/alumni/update-profile-picture"
      method="post"
      enctype="multipart/form-data"
    >
      <div class="profile-picture-container">
        <img
          v-if="previewImageUrl || member?.imageUrl"
          :src="previewImageUrl || member?.imageUrl || undefined"
          alt="alumni member profile picture"
          class="image"
        />
        <SvgUserCircle v-else class="user-icon" />
        <label for="profile-picture-upload" class="upload-label button-link -secondary">
          {{ t('uploadPhoto') }}
        </label>
        <input
          id="profile-picture-upload"
          ref="profilePictureElement"
          type="file"
          name="_profilePicture"
          accept="image/*"
          class="file-input"
          @change="onProfilePictureChange"
        />
      </div>
      <div v-if="profilePictureError" class="error-message copy-small">
        {{ t('error.profilePicture') }}
      </div>
    </form>
    <form novalidate class="fields" @submit.prevent="submit()">
      <FormInput
        ref="salutationInput"
        v-model="contactDetails.salutation"
        type="text"
        name="salutation"
        class="salutation"
        autocomplete="honorific-prefix"
        :label="t('salutation')"
      />
      <FormInput
        ref="nameAffixInput"
        v-model="contactDetails.nameAffix"
        type="text"
        name="nameAffix"
        class="name-affix"
        autocomplete="honorific-suffix"
        :label="t('nameAffix')"
      />
      <FormInput
        ref="firstNameInput"
        v-model="contactDetails.firstName"
        type="text"
        name="firstName"
        class="first-name"
        autocomplete="given-name"
        :label="t('firstName')"
        :error="t('error.firstName')"
        required
      />
      <FormInput
        ref="familyNameInput"
        v-model="contactDetails.familyName"
        type="text"
        name="familyName"
        class="family-name"
        autocomplete="family-name"
        :label="t('familyName')"
        :error="t('error.familyName')"
        required
      />
      <FormInput
        ref="streetInput"
        v-model="contactDetails.street"
        type="text"
        name="street"
        class="street"
        autocomplete="street-address"
        :label="t('street')"
      />
      <FormInput
        ref="zipInput"
        v-model="contactDetails.zip"
        type="text"
        name="zip"
        class="zip"
        autocomplete="postal-code"
        :label="t('zip')"
      />
      <FormInput
        ref="cityInput"
        v-model="contactDetails.city"
        type="text"
        name="city"
        class="city"
        autocomplete="address-level2"
        :label="t('city')"
      />
      <FormInput
        ref="dateOfBirthInput"
        v-model="contactDetails.dateOfBirth"
        type="date"
        name="dateOfBirth"
        class="date-of-birth"
        :label="t('dateOfBirth')"
        :error="t('error.dateOfBirth')"
        required
      />
      <FormInput
        ref="graduationYearInput"
        v-model="customFields.graduationYear"
        type="number"
        name="graduationYear"
        class="graduation-year"
        min="1000"
        max="9999"
        :custom-validation="(val?: string | null) => !val || val.match(/^\d{4}$/) !== null"
        :error="t('error.graduationYear')"
        :label="t('graduationYear')"
      />
      <FormInput
        ref="fieldOfStudyInput"
        v-model="customFields.fieldOfStudy"
        type="text"
        name="fieldOfStudy"
        class="field-of-study"
        :label="t('fieldOfStudy')"
      />
      <FormInput
        ref="occupationInput"
        v-model="customFields.occupation"
        type="text"
        name="occupation"
        class="occupation"
        autocomplete="organization-title"
        :label="t('occupation')"
      />
      <FormInput
        ref="employerInput"
        v-model="customFields.employer"
        type="text"
        name="employer"
        class="employer"
        autocomplete="organization"
        :label="t('employer')"
      />
      <FormInput
        ref="linkedInInput"
        v-model="customFields.linkedIn"
        type="url"
        name="linkedIn"
        class="linkedin-profile"
        autocomplete="url"
        :custom-validation="(val?: string | null) => !val || val.match(/^https?:\/\/(www\.)?linkedin\.com\//) !== null"
        :label="t('linkedinProfile')"
        :placeholder="t('linkedinProfilePlaceholder')"
        :error="t('error.linkedinProfile')"
      />
      <FormInput
        ref="phoneInput"
        v-model="contactDetails.privatePhone"
        type="tel"
        name="phone"
        class="phone"
        autocomplete="tel"
        :label="t('phone')"
        :error="t('error.phone')"
      />
      <FormInput
        ref="emailInput"
        v-model="contactDetails.primaryEmail"
        type="email"
        name="email"
        class="email"
        autocomplete="email"
        :label="t('email')"
        :error="t('error.email')"
        :disabled="!emailEditable"
        required
      />

      <div v-if="usePrivacy" class="privacy-checkbox" :class="{ '-error': privacyError }">
        <input
          id="privacyAccepted"
          ref="privacyCheckboxElement"
          v-model="privacyPolicyAccepted"
          name="privacyAccepted"
          type="checkbox"
          class="checkbox-input"
          required
          @change="onPrivacyChange"
          @blur="validatePrivacyCheckbox"
        />
        <label for="privacyAccepted" class="checkbox-label copy-regular">
          {{ t('privacyTextStart') }}
          <a :href="t('privacyPolicyUrl')" target="_blank" class="button-link -secondary privacy-link">{{
            t('privacyPolicy')
          }}</a>
          {{ t('privacyTextEnd') }}*
        </label>
      </div>

      <div v-if="usePrivacy" class="whatsapp-checkbox">
        <input id="whatsAppOptIn" v-model="whatsAppOptIn" name="whatsAppOptIn" type="checkbox" class="checkbox-input" />
        <label for="whatsAppOptIn" class="checkbox-label copy-regular">
          {{ t('whatsAppOptIn') }}
        </label>
      </div>

      <div v-if="error" class="error">{{ error }}</div>

      <button
        class="button-secondary submit-button"
        type="submit"
        :disabled="(cannotSendWhenNothingChanged && !isDirty) || disabled"
        style="grid-column: 1 / -1"
      >
        {{ sendLabel }}
      </button>
    </form>
  </div>
</template>

<script setup lang="ts">
import { reactive, ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import SvgUserCircle from '~/components/content-modules/svgs/SvgUserCircle.vue';

export type AlumniFormSubmitEventData = {
  contactDetails: any;
  customFields: any;
  privacyPolicyAccepted: boolean;
  profilePictureFormData: FormData | null;
};

type MemberWithImage = { imageUrl?: string | null };

const { t } = useI18n();

const props = defineProps<{
  member: MemberWithImage | null;
  sendLabel: string;
  emailEditable: boolean;
  cannotSendWhenNothingChanged?: boolean;
  disabled?: boolean;
  usePrivacy?: boolean;
  error?: string;
}>();

const emit = defineEmits<{
  (e: 'submit', value: AlumniFormSubmitEventData): void;
}>();

const profilePictureForm = ref<HTMLFormElement>();

const previewImageUrl = ref<string | undefined>(undefined);

const profilePictureElement = ref<HTMLInputElement>();
const profilePictureError = ref<boolean>(false);
const privacyCheckboxElement = ref<HTMLInputElement>();
const privacyError = ref<boolean>(false);

const salutationInput = ref();
const nameAffixInput = ref();
const firstNameInput = ref();
const familyNameInput = ref();
const streetInput = ref();
const zipInput = ref();
const cityInput = ref();
const dateOfBirthInput = ref();
const graduationYearInput = ref();
const fieldOfStudyInput = ref();
const occupationInput = ref();
const employerInput = ref();
const linkedInInput = ref();
const phoneInput = ref();
const emailInput = ref();

const formInputs = computed(() => {
  return [
    salutationInput.value,
    nameAffixInput.value,
    firstNameInput.value,
    familyNameInput.value,
    streetInput.value,
    zipInput.value,
    cityInput.value,
    dateOfBirthInput.value,
    graduationYearInput.value,
    fieldOfStudyInput.value,
    occupationInput.value,
    employerInput.value,
    linkedInInput.value,
    phoneInput.value,
    emailInput.value,
  ].filter(Boolean);
});

const contactDetails = reactive({
  salutation: '',
  nameAffix: '',
  firstName: '',
  familyName: '',
  street: '',
  city: '',
  zip: '',
  country: '',
  dateOfBirth: '',
  privatePhone: '',
  primaryEmail: '',
});

const customFields = reactive({
  graduationYear: '',
  fieldOfStudy: '',
  occupation: '',
  employer: '',
  linkedIn: '',
});

const whatsAppOptIn = ref(false);
const privacyPolicyAccepted = ref(false);
const isDirty = ref(false);

function validateAllFormInputs() {
  let isValid = true;

  if (props.usePrivacy && !privacyPolicyAccepted.value) {
    privacyError.value = true;
    isValid = false;
  }

  formInputs.value.forEach((formInput) => {
    if (formInput && typeof formInput.validateForm === 'function') {
      if (!formInput.validateForm()) {
        isValid = false;
      }
    }
  });

  return isValid;
}

function onProfilePictureChange(event: Event) {
  const input = event.target as HTMLInputElement;
  if (input.files && input.files[0]) {
    const file = input.files[0];

    // Validate file extension
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (!['png', 'jpg', 'jpeg', 'gif'].includes(fileExtension || '')) {
      profilePictureError.value = true;
      return;
    }

    // Validate file size (3MB)
    if (file.size > 3 * 1024 * 1024) {
      profilePictureError.value = true;
      return;
    }

    if (previewImageUrl.value) {
      URL.revokeObjectURL(previewImageUrl.value);
    }
    previewImageUrl.value = URL.createObjectURL(file);
    profilePictureError.value = false; // Clear error on successful upload
  } else {
    if (previewImageUrl.value) {
      URL.revokeObjectURL(previewImageUrl.value);
    }
    previewImageUrl.value = undefined;
    profilePictureError.value = false; // Clear error when no file selected
  }
  isDirty.value = true;
}

function updateFormData(m: any) {
  if (m) {
    contactDetails.salutation = m.salutation;
    contactDetails.nameAffix = m.nameAffix;
    contactDetails.firstName = m.firstName;
    contactDetails.familyName = m.familyName;
    contactDetails.street = m.street;
    contactDetails.city = m.city;
    contactDetails.zip = m.zip;
    contactDetails.dateOfBirth = m.dateOfBirth;
    customFields.graduationYear = m.graduationYear ?? '';
    customFields.fieldOfStudy = m.fieldOfStudy ?? '';
    customFields.occupation = m.occupation ?? '';
    customFields.employer = m.employer ?? '';
    customFields.linkedIn = m.linkedIn ?? '';
    whatsAppOptIn.value = m.whatsAppOptIn ?? false;
    contactDetails.privatePhone = m.privatePhone ?? '';
    contactDetails.primaryEmail = m.primaryEmail ?? '';
  }
}

watch(
  () => props.member,
  () => {
    if (props.member) {
      updateFormData(props.member);
      if (previewImageUrl.value) {
        URL.revokeObjectURL(previewImageUrl.value);
        previewImageUrl.value = undefined;
      }
      nextTick(() => (isDirty.value = false));
    }
  },
  {
    immediate: true,
  },
);

function onPrivacyChange() {
  if (privacyCheckboxElement.value?.checked) {
    privacyError.value = false; // Clear error immediately when checked
  }
}

function validatePrivacyCheckbox() {
  if (props.usePrivacy && privacyCheckboxElement.value) {
    const currentValue = privacyCheckboxElement.value.checked;
    privacyError.value = !currentValue;
  }
}

watch([contactDetails, customFields, whatsAppOptIn], () => {
  isDirty.value = true;
});

async function submit() {
  if (!validateAllFormInputs()) {
    return;
  }

  const formData = profilePictureForm.value ? new FormData(profilePictureForm.value) : null;

  emit('submit', {
    contactDetails,
    customFields: {
      ...customFields,
      whatsAppOptIn: whatsAppOptIn.value,
    },
    privacyPolicyAccepted: privacyPolicyAccepted.value,
    profilePictureFormData: previewImageUrl.value && formData?.get('_profilePicture') ? formData : null,
  });
}
</script>

<style scoped lang="scss">
.alumni-form {
  > .profile-picture-upload {
    margin: var(--spacing-xs) 0;
    position: relative;

    @media (--xs), (--sm) {
      gap: var(--spacing-2xs);
    }

    @media (--md), (--lg) {
      gap: var(--spacing-xs);
    }

    > .profile-picture-container:has(.file-input:focus-visible) {
      > .upload-label {
        border-radius: 4px;
        border: 2px solid var(--color-sky);
      }
    }

    &.-error {
      > .profile-picture-container > .upload-label {
        color: var(--color-error-red);
        border-color: var(--color-error-red);
      }

      > .error-message {
        color: var(--color-error-red);
        margin-top: var(--spacing-4xs);
      }
    }

    > .profile-picture-container {
      display: flex;
      align-items: center;
      gap: var(--spacing-xs);

      > .image {
        width: 6.375rem;
        aspect-ratio: 1 / 1;
        border-radius: 50%;
        object-fit: cover;
      }

      > .file-input {
        position: absolute;
        opacity: 0;
        width: 0;
        height: 0;
        pointer-events: none;
      }
    }
  }

  > .fields {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: var(--spacing-xs);

    > .form-input {
      @media (--xs), (--sm) {
        grid-column: 1 / -1;
      }
    }

    > .salutation,
    > .first-name,
    > .date-of-birth,
    > .phone {
      @media (--md), (--lg) {
        grid-column: 1 / 3;
      }
    }

    > .name-affix,
    > .family-name,
    > .graduation-year,
    > .email {
      @media (--md), (--lg) {
        grid-column: 3 / -1;
      }
    }

    > .street,
    > .field-of-study,
    > .occupation,
    > .employer,
    > .linkedin-profile,
    > .privacy-checkbox {
      grid-column: 1 / -1;
    }

    > .whatsapp-checkbox {
      grid-column: 1 / -1;
      margin-top: calc(var(--spacing-xs) / -2);
    }

    > .zip {
      @media (--md), (--lg) {
        grid-column: 1 / 2;
      }
    }

    > .city {
      @media (--md), (--lg) {
        grid-column: 2 / -1;
      }
    }

    > .whatsapp-checkbox,
    > .privacy-checkbox {
      display: flex;
      gap: var(--spacing-4xs);

      &.-error {
        > .checkbox-input {
          border-color: var(--color-error-red);
        }

        > .checkbox-label {
          color: var(--color-error-red);

          > .privacy-link {
            color: var(--color-error-red);
            border-color: var(--color-error-red);
          }
        }
      }

      > .checkbox-input {
        appearance: none;
        width: 1.125rem;
        height: 1.125rem;
        flex-shrink: 0;
        border: 1px solid var(--color-black);
        background-color: transparent;
        margin-top: 0.25rem;

        &:checked {
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='17' height='16' viewBox='0 0 17 16' fill='none'%3E%3Cpath d='M14.5 3.33398L7.16667 12.6673L2.5 8.66732' stroke='%23272727' stroke-width='2' stroke-miterlimit='10'/%3E%3C/svg%3E");
        }
      }
    }

    > .error {
      grid-column: 1 / -1;
      color: var(--color-error-red);
    }

    > .submit-button {
      justify-self: start;
    }
  }
}
</style>

<i18n lang="yaml">
de:
  uploadPhoto: Foto hochladen
  salutation: Anrede
  nameAffix: Titel
  firstName: Vorname*
  familyName: Nachname*
  street: Strasse
  zip: Postleitzahl
  city: Ort
  dateOfBirth: Geburtstag*
  graduationYear: Maturitätsjahr (4-stellig)
  fieldOfStudy: Studium
  maidenName: Lediger Name
  occupation: Beruf
  employer: Arbeitgeber
  linkedinProfile: Linkedinprofil
  linkedinProfilePlaceholder: https://www.linkedin.com/in/ihr-name/
  phone: Telefon
  email: E-Mail*
  whatsAppOptIn: Einladung in WhatsApp-Gruppe erlauben.
  privacyTextStart: Ich habe die
  privacyPolicy: Datenschutzbestimmungen
  privacyPolicyUrl: /de/datenschutz
  privacyTextEnd: gelesen.
  profile:
    send: Absenden
  error:
    firstName: Bitte geben Sie Ihren Vornamen ein.
    familyName: Bitte geben Sie Ihren Nachnamen ein.
    dateOfBirth: Bitte geben Sie Ihren Geburtstag ein.
    email: Bitte geben Sie eine gültige E-Mail-Adresse ein.
    profilePicture: Bitte laden Sie ein gültiges Bildformat hoch (JPEG, JPG, PNG oder GIF, max 3MB).
    phone: Bitte geben Sie eine gültige Telefonnummer ein (z.B. +41 79 123 45 67).
    linkedinProfile: Bitte geben Sie eine gültige LinkedIn-Profil-URL ein (https://www.linkedin.com/in/ihr-name/).
    graduationYear: Bitte geben Sie ein gültiges Jahr ein (4-stellig).

en:
  uploadPhoto: Upload photo
  salutation: Salutation
  nameAffix: Title
  firstName: First Name*
  familyName: Last Name*
  street: Street
  zip: Postal Code
  city: City
  dateOfBirth: Birthday*
  graduationYear: Graduation Year (4 digits)
  fieldOfStudy: Field of Study
  maidenName: Maiden Name
  occupation: Profession
  employer: Employer
  linkedinProfile: LinkedIn Profile
  linkedinProfilePlaceholder: https://www.linkedin.com/in/your-name/
  phone: Phone
  email: Email*
  whatsAppOptIn: Allow invitation to Alumni WhatsApp group.
  privacyTextStart: I have read the
  privacyPolicy: privacy policy
  privacyPolicyUrl: /en/data-protection
  privacyTextEnd: .
  profile:
    send: Send
  error:
    firstName: Please enter your first name.
    familyName: Please enter your last name.
    dateOfBirth: Please enter your date of birth.
    email: Please enter a valid email address.
    profilePicture: Please upload a valid image format (JPEG, JPG, PNG or GIF, max 3MB).
    phone: Please enter a valid phone number (e.g. +41 79 123 45 67).
    linkedinProfile: Please enter a valid LinkedIn profile URL (https://www.linkedin.com/in/your-name/).
    graduationYear: Please enter a valid year (4 digits).
</i18n>
